import React, { useState, useEffect, useMemo } from "react";
import { MainLayout } from "@/components/MainLayout";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { CardLoader, PageLoader } from "@/components/MoneyMachineLoader";
import { RiskBadge } from "@/components/RiskBadge";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  CreditCard,
  Calculator,
  Trash2,
  Edit,
  CalendarIcon,
  Building,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Banknote,
  CircleDollarSign,
  Clock,
  ArrowDownRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Landmark,
} from "lucide-react";
import { toast } from "sonner";
import { format, addMonths } from "date-fns";
import axios from "axios";
import { SortableHeader, applySorting } from "@/components/SortableHeader";
import { InfoTip } from "@/components/InfoTip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Collateral Combobox component
const CollateralSelect = ({ assets, selectedIds, onSelect, formatCurrency }) => {
  const [open, setOpen] = useState(false);

  const selectedLabel = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) return "None (no collateral)";
    if (selectedIds.includes("other")) return "Other";
    const names = assets
      .filter((a) => selectedIds.includes(a.asset_id))
      .map((a) => a.name);
    if (names.length === 0) return "None (no collateral)";
    if (names.length <= 2) return names.join(", ");
    return `${names.length} assets selected`;
  }, [selectedIds, assets]);

  const toggleAsset = (id) => {
    if (id === "none") {
      onSelect([]);
      setOpen(false);
      return;
    }
    if (id === "other") {
      onSelect(["other"]);
      setOpen(false);
      return;
    }
    // Remove special values when selecting actual assets
    const current = selectedIds.filter((x) => x !== "other");
    if (current.includes(id)) {
      onSelect(current.filter((x) => x !== id));
    } else {
      onSelect([...current, id]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="collateral-select-trigger"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" style={{ zIndex: 9999 }}>
        <Command>
          <CommandInput placeholder="Search assets..." />
          <CommandList>
            <CommandEmpty>No assets found.</CommandEmpty>
            <CommandGroup heading="Options">
              <CommandItem
                value="none"
                onSelect={() => toggleAsset("none")}
                data-testid="collateral-option-none"
              >
                <Check className={`mr-2 h-4 w-4 ${selectedIds.length === 0 ? "opacity-100" : "opacity-0"}`} />
                <span>None (no collateral)</span>
              </CommandItem>
              <CommandItem
                value="other"
                onSelect={() => toggleAsset("other")}
                data-testid="collateral-option-other"
              >
                <Check className={`mr-2 h-4 w-4 ${selectedIds.includes("other") ? "opacity-100" : "opacity-0"}`} />
                <span>Other</span>
              </CommandItem>
            </CommandGroup>
            {assets.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Portfolio Assets">
                  {assets.map((asset) => (
                    <CommandItem
                      key={asset.asset_id}
                      value={`${asset.name} ${asset.symbol || ""}`}
                      onSelect={() => toggleAsset(asset.asset_id)}
                      data-testid={`collateral-option-${asset.asset_id}`}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          selectedIds.includes(asset.asset_id) ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <div className="flex items-center justify-between w-full">
                        <span>{asset.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {formatCurrency((asset.current_price || asset.purchase_price) * asset.quantity)}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Credit form component
const CreditForm = ({ formData, setFormData, assets, formatCurrency, isEdit = false }) => {
  const { t } = useI18n();
  // Auto-calculate end date when start_date or term_months changes
  const calculatedEndDate = useMemo(() => {
    if (!formData.start_date || !formData.term_months) return null;
    return addMonths(formData.start_date, parseInt(formData.term_months) || 0);
  }, [formData.start_date, formData.term_months]);

  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Credit Name</Label>
          <Input
            data-testid="credit-name-input"
            placeholder="e.g., Home Mortgage, Car Loan"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <Label>{t("credits.creditType") || "Credit Type"}</Label>
          <Select
            value={formData.credit_type || "other"}
            onValueChange={(v) => setFormData({ ...formData, credit_type: v })}
          >
            <SelectTrigger data-testid="credit-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mortgage">{t("credits.typeMortgage") || "Mortgage"}</SelectItem>
              <SelectItem value="auto_loan">{t("credits.typeAutoLoan") || "Auto Loan"}</SelectItem>
              <SelectItem value="student_loan">{t("credits.typeStudentLoan") || "Student Loan"}</SelectItem>
              <SelectItem value="personal_loan">{t("credits.typePersonalLoan") || "Personal Loan"}</SelectItem>
              <SelectItem value="business_loan">{t("credits.typeBusinessLoan") || "Business Loan"}</SelectItem>
              <SelectItem value="credit_card">{t("credits.typeCreditCard") || "Credit Card"}</SelectItem>
              <SelectItem value="lombard">{t("credits.typeLombard") || "Lombard / Margin"}</SelectItem>
              <SelectItem value="other">{t("credits.typeOther") || "Other"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label>Lender</Label>
          <Input
            placeholder="Bank name or lender"
            value={formData.lender}
            onChange={(e) => setFormData({ ...formData, lender: e.target.value })}
          />
        </div>
        <div>
          <Label>Principal Amount</Label>
          <Input
            data-testid="credit-principal-input"
            type="number"
            step="any"
            placeholder="0.00"
            value={formData.principal_amount}
            onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
          />
        </div>
        <div>
          <Label>Outstanding Amount</Label>
          <Input
            type="number"
            step="any"
            placeholder="0.00"
            value={formData.outstanding_amount}
            onChange={(e) => setFormData({ ...formData, outstanding_amount: e.target.value })}
          />
        </div>
        <div>
          <Label>Interest Rate (%)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="5.5"
            value={formData.interest_rate}
            onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
          />
        </div>
        <div>
          <Label>Monthly Payment</Label>
          <Input
            type="number"
            step="any"
            placeholder="0.00"
            value={formData.monthly_payment}
            onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
          />
        </div>
        <div>
          <Label>Currency</Label>
          <Select value={formData.currency || "USD"} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
            <SelectTrigger data-testid="credit-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ zIndex: 9999 }}>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="GBP">GBP</SelectItem>
              <SelectItem value="CHF">CHF</SelectItem>
              <SelectItem value="BTC">BTC</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Start Date */}
        <div>
          <Label>Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="start-date-trigger">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(formData.start_date, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} style={{ zIndex: 9999 }}>
              <Calendar
                mode="single"
                selected={formData.start_date}
                onSelect={(date) => date && setFormData({ ...formData, start_date: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Term (months) */}
        <div>
          <Label>Term (months)</Label>
          <Input
            data-testid="credit-term-input"
            type="number"
            min="1"
            placeholder="12"
            value={formData.term_months}
            onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
          />
        </div>

        {/* Auto-calculated end date */}
        <div className="col-span-2">
          <Label className="text-muted-foreground text-xs">Calculated End Date</Label>
          <div className="mt-1 p-2 bg-secondary/50 rounded-md text-sm font-mono" data-testid="calculated-end-date">
            {calculatedEndDate ? format(calculatedEndDate, "PPP") : "Set start date and term"}
          </div>
        </div>

        {/* Payment Schedule */}
        <div className="col-span-2">
          <Label>Payment Date in Month</Label>
          <Select
            value={formData.payment_schedule}
            onValueChange={(v) => setFormData({ ...formData, payment_schedule: v })}
          >
            <SelectTrigger data-testid="payment-schedule-select">
              <SelectValue placeholder="Select payment timing" />
            </SelectTrigger>
            <SelectContent style={{ zIndex: 9999 }}>
              <SelectItem value="beginning">Beginning of the month (1st-5th)</SelectItem>
              <SelectItem value="mid">Mid-month (13th-17th)</SelectItem>
              <SelectItem value="end">End of the month (25th-30th)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Collateral */}
        <div className="col-span-2">
          <Label>Collateral Asset</Label>
          <div className="mt-1">
            <CollateralSelect
              assets={assets}
              selectedIds={formData.collateral_asset_ids}
              onSelect={(ids) => setFormData({ ...formData, collateral_asset_ids: ids })}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>

        {/* Risk Score */}
        <div className="col-span-2">
          <Label>Risk Score: {formData.risk_score}</Label>
          <Slider
            value={[formData.risk_score]}
            onValueChange={([v]) => setFormData({ ...formData, risk_score: v })}
            max={100}
            step={1}
            className="mt-2"
          />
        </div>

        {/* Notes (only in edit) */}
        {(isEdit || !isEdit) && (
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              data-testid="credit-notes"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const CreditsPage = () => {
  const {
    credits,
    assets,
    creditCapacity,
    loading,
    formatCurrency,
    createCredit,
    updateCredit,
    deleteCredit,
  } = usePortfolio();
  const { t } = useI18n();
  const { isDemo } = useAuth();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [creditSort, setCreditSort] = useState({ key: "outstanding", dir: "desc" });

  const defaultForm = {
    name: "",
    credit_type: "other",
    lender: "",
    principal_amount: "",
    outstanding_amount: "",
    interest_rate: "",
    monthly_payment: "",
    currency: "USD",
    collateral_asset_ids: [],
    start_date: new Date(),
    term_months: "12",
    payment_schedule: "end",
    risk_score: 50,
    notes: "",
  };

  const [formData, setFormData] = useState(defaultForm);

  const resetForm = () => setFormData(defaultForm);

  const CREDIT_TYPES = [
    { value: "mortgage", label: t("credits.typeMortgage") || "Mortgage" },
    { value: "auto_loan", label: t("credits.typeAutoLoan") || "Auto Loan" },
    { value: "student_loan", label: t("credits.typeStudentLoan") || "Student Loan" },
    { value: "personal_loan", label: t("credits.typePersonalLoan") || "Personal Loan" },
    { value: "business_loan", label: t("credits.typeBusinessLoan") || "Business Loan" },
    { value: "credit_card", label: t("credits.typeCreditCard") || "Credit Card" },
    { value: "lombard", label: t("credits.typeLombard") || "Lombard / Margin" },
    { value: "other", label: t("credits.typeOther") || "Other" },
  ];

  const creditTypeLabel = (type) => CREDIT_TYPES.find(ct => ct.value === type)?.label || type;

  const filteredCredits = filterType === "all" ? credits : credits.filter(c => c.credit_type === filterType);

  // Fetch payment summary
  useEffect(() => {
    const fetchPaymentSummary = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/credits/payment-summary`, { withCredentials: true });
        setPaymentSummary(resp.data);
      } catch (err) {
        console.error("Payment summary error:", err);
      }
    };
    if (!loading) fetchPaymentSummary();
  }, [credits, loading]);

  const handleAdd = async () => {
    try {
      await createCredit({
        ...formData,
        principal_amount: parseFloat(formData.principal_amount) || 0,
        outstanding_amount: parseFloat(formData.outstanding_amount) || parseFloat(formData.principal_amount) || 0,
        interest_rate: parseFloat(formData.interest_rate) || 0,
        monthly_payment: parseFloat(formData.monthly_payment) || 0,
        term_months: parseInt(formData.term_months) || 12,
        start_date: formData.start_date.toISOString(),
      });
      toast.success("Credit added successfully");
      setIsAddOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to add credit");
    }
  };

  const handleEdit = async () => {
    try {
      await updateCredit(selectedCredit.credit_id, {
        name: formData.name,
        outstanding_amount: parseFloat(formData.outstanding_amount),
        monthly_payment: parseFloat(formData.monthly_payment),
        collateral_asset_ids: formData.collateral_asset_ids,
        term_months: parseInt(formData.term_months) || 12,
        payment_schedule: formData.payment_schedule,
        risk_score: formData.risk_score,
        notes: formData.notes,
      });
      toast.success("Credit updated successfully");
      setIsEditOpen(false);
      setSelectedCredit(null);
      resetForm();
    } catch (error) {
      toast.error("Failed to update credit");
    }
  };

  const confirmDelete = (credit) => {
    setDeleteTarget(credit);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCredit(deleteTarget.credit_id);
      toast.success("Credit deleted successfully");
    } catch (error) {
      toast.error("Failed to delete credit");
    } finally {
      setIsDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  const openEdit = (credit) => {
    setSelectedCredit(credit);
    setFormData({
      name: credit.name,
      credit_type: credit.credit_type || "other",
      lender: credit.lender,
      principal_amount: credit.principal_amount.toString(),
      outstanding_amount: credit.outstanding_amount.toString(),
      interest_rate: credit.interest_rate.toString(),
      monthly_payment: credit.monthly_payment.toString(),
      currency: credit.currency,
      collateral_asset_ids: credit.collateral_asset_ids || [],
      start_date: new Date(credit.start_date),
      term_months: (credit.term_months || 12).toString(),
      payment_schedule: credit.payment_schedule || "end",
      risk_score: credit.risk_score || 50,
      notes: credit.notes || "",
    });
    setIsEditOpen(true);
  };

  if (loading) {
    return <PageLoader />;
  }

  const totalMonthlyPayments = credits.reduce((sum, c) => sum + (c.monthly_payment || 0), 0);
  const totalOutstanding = credits.reduce((sum, c) => sum + (c.outstanding_amount || 0), 0);

  const paymentScheduleLabel = (schedule) => {
    const labels = { beginning: "1st-5th", mid: "13th-17th", end: "25th-30th" };
    return labels[schedule] || schedule;
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="credits-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
              {t("credits.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("credits.subtitle")}</p>
          </div>

          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-credit-btn"
                disabled={isDemo}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs glow-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("credits.addCredit")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase">{t("credits.addNew")}</DialogTitle>
                <DialogDescription>Enter the details of your credit/loan</DialogDescription>
              </DialogHeader>
              <CreditForm
                formData={formData}
                setFormData={setFormData}
                assets={assets}
                formatCurrency={formatCurrency}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAdd}
                  data-testid="save-credit-btn"
                  className="bg-primary text-primary-foreground"
                >
                  Add Credit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Payment Overview */}
        {paymentSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payment-overview">
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("credits.totalPaid")}<InfoTip text={t("credits.tipTotalPaid")} />
                  </p>
                  <Check className="w-4 h-4 text-green-500" />
                </div>
                <p className="text-2xl font-mono font-bold text-green-500" data-testid="total-paid-value">
                  {formatCurrency(paymentSummary.total_paid)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("credits.paidLastMonth")}<InfoTip text={t("credits.tipPaidLastMonth")} />
                  </p>
                  <Clock className="w-4 h-4 text-blue-400" />
                </div>
                <p className="text-2xl font-mono font-bold text-blue-400" data-testid="paid-last-month-value">
                  {formatCurrency(paymentSummary.paid_last_month)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("credits.totalRemaining")}<InfoTip text={t("credits.tipTotalRemaining")} />
                  </p>
                  <CircleDollarSign className="w-4 h-4 text-orange-400" />
                </div>
                <p className="text-2xl font-mono font-bold text-orange-400" data-testid="total-remaining-value">
                  {formatCurrency(paymentSummary.total_remaining)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                    {t("credits.nextMonth")}<InfoTip text={t("credits.tipNextMonth")} />
                  </p>
                  <ArrowDownRight className="w-4 h-4 text-red-400" />
                </div>
                <p className="text-2xl font-mono font-bold text-red-400" data-testid="next-month-value">
                  {formatCurrency(paymentSummary.next_month_payment)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credit Capacity Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="md:col-span-2" data-testid="credit-capacity-overview">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                {t("credits.creditCapacity")}<InfoTip text={t("credits.tipCreditCapacity")} />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">{t("credits.utilization")}<InfoTip text={t("credits.tipUtilization")} /></span>
                    <span className="font-mono font-semibold">
                      {(creditCapacity?.utilization_rate || 0).toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={creditCapacity?.utilization_rate || 0} className="h-3" />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">{t("credits.totalCapacity")}</p>
                    <p className="text-xl font-mono font-bold text-primary">
                      {formatCurrency(creditCapacity?.total_credit_capacity || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">{t("credits.available")}</p>
                    <p className="text-xl font-mono font-bold text-green-500">
                      {formatCurrency(creditCapacity?.available_credit || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-outstanding-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                  {t("credits.totalOutstanding")}<InfoTip text={t("credits.tipTotalOutstanding")} />
                </p>
                <CreditCard className="w-5 h-5 text-primary" />
              </div>
              <p className="text-3xl font-mono font-bold">{formatCurrency(totalOutstanding)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {credits.length} {credits.length !== 1 ? t("credits.activeCreditsPlural") : t("credits.activeCredits")}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="monthly-payments-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground/80">
                  {t("credits.monthlyPayments")}<InfoTip text={t("credits.tipMonthlyPayments")} />
                </p>
                <TrendingUp className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-3xl font-mono font-bold text-red-400">
                {formatCurrency(totalMonthlyPayments)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{t("credits.totalPerMonth")}</p>
            </CardContent>
          </Card>
        </div>

        {/* Credits Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold tracking-widest uppercase text-muted-foreground/80">
              Your Credits
            </CardTitle>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="credit-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("credits.allTypes") || "All Types"}</SelectItem>
                {CREDIT_TYPES.map(ct => (
                  <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            {filteredCredits.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Credit" sortKey="name" currentSort={creditSort} onSort={setCreditSort} />
                    <SortableHeader label="Lender" sortKey="lender" currentSort={creditSort} onSort={setCreditSort} />
                    <SortableHeader label="Principal" sortKey="principal" currentSort={creditSort} onSort={setCreditSort} className="text-right" />
                    <SortableHeader label="Outstanding" sortKey="outstanding" currentSort={creditSort} onSort={setCreditSort} className="text-right" />
                    <SortableHeader label="Rate" sortKey="rate" currentSort={creditSort} onSort={setCreditSort} className="text-right" />
                    <SortableHeader label="Monthly" sortKey="monthly" currentSort={creditSort} onSort={setCreditSort} className="text-right" />
                    <TableHead className="text-center">Payment</TableHead>
                    <SortableHeader label="Risk" sortKey="risk" currentSort={creditSort} onSort={setCreditSort} className="text-center" />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applySorting(filteredCredits, creditSort, {
                    name: c => (c.name || "").toLowerCase(),
                    lender: c => (c.lender || "").toLowerCase(),
                    principal: c => c.principal_amount || 0,
                    outstanding: c => c.outstanding_amount || 0,
                    rate: c => c.interest_rate || 0,
                    monthly: c => c.monthly_payment || 0,
                    risk: c => c.risk_score || 50,
                  }).map((credit) => {
                    const payoffProgress =
                      credit.principal_amount > 0
                        ? ((credit.principal_amount - credit.outstanding_amount) / credit.principal_amount) * 100
                        : 0;
                    const hasDetail = credit.source === "plaid" && credit.plaid_detail;
                    const isExpanded = expandedRows[credit.credit_id];
                    const detail = credit.plaid_detail || {};
                    const creditCcy = credit.currency || "USD";

                    return (
                      <React.Fragment key={credit.credit_id}>
                      <TableRow data-testid={`credit-row-${credit.credit_id}`} className={hasDetail ? "cursor-pointer" : ""} onClick={() => hasDetail && setExpandedRows(prev => ({...prev, [credit.credit_id]: !prev[credit.credit_id]}))}>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="font-medium">{credit.name}</p>
                              {credit.source === "plaid" && (
                                <Badge variant="outline" className="text-[9px] px-1 py-0 border-blue-500/40 text-blue-400">Linked</Badge>
                              )}
                              {hasDetail && (
                                isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{creditTypeLabel(credit.credit_type)}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {credit.term_months ? `${credit.term_months}mo` : ""}
                              </span>
                              <span className="text-[10px] font-mono text-muted-foreground/60">{creditCcy}</span>
                            </div>
                            <Progress value={payoffProgress} className="h-1 mt-1 w-24" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-muted-foreground" />
                            {credit.lender}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(credit.principal_amount, true, creditCcy)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(credit.outstanding_amount, true, creditCcy)}
                        </TableCell>
                        <TableCell className="text-right font-mono">{credit.interest_rate}%</TableCell>
                        <TableCell className="text-right font-mono text-red-400">
                          {formatCurrency(credit.monthly_payment, true, creditCcy)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">
                            {paymentScheduleLabel(credit.payment_schedule)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <RiskBadge score={credit.risk_score} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" disabled={isDemo} onClick={() => openEdit(credit)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" disabled={isDemo} onClick={() => confirmDelete(credit)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Plaid Detail Expanded Row */}
                      {hasDetail && isExpanded && (
                        <TableRow className="bg-accent/20 hover:bg-accent/30">
                          <TableCell colSpan={9} className="py-3 px-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              {credit.credit_type === "mortgage" && (
                                <>
                                  {detail.loan_type && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Loan Type</p>
                                      <p className="font-medium capitalize">{detail.loan_type}</p>
                                    </div>
                                  )}
                                  {detail.loan_term && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Term</p>
                                      <p className="font-medium">{detail.loan_term}</p>
                                    </div>
                                  )}
                                  {detail.origination_date && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Origination</p>
                                      <p className="font-medium">{detail.origination_date}</p>
                                    </div>
                                  )}
                                  {detail.origination_principal != null && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Original Principal</p>
                                      <p className="font-medium font-mono">{formatCurrency(detail.origination_principal)}</p>
                                    </div>
                                  )}
                                  {detail.maturity_date && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Maturity Date</p>
                                      <p className="font-medium">{detail.maturity_date}</p>
                                    </div>
                                  )}
                                  {detail.interest_rate_type && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Rate Type</p>
                                      <p className="font-medium capitalize">{detail.interest_rate_type}</p>
                                    </div>
                                  )}
                                  {detail.property_address && (
                                    <div className="col-span-2">
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" />Property</p>
                                      <p className="font-medium">{detail.property_address}</p>
                                    </div>
                                  )}
                                  {detail.next_monthly_payment != null && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Next Payment</p>
                                      <p className="font-medium font-mono">{formatCurrency(detail.next_monthly_payment)}</p>
                                      {detail.next_payment_due_date && <p className="text-[10px] text-muted-foreground">Due {detail.next_payment_due_date}</p>}
                                    </div>
                                  )}
                                  {detail.escrow_balance != null && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Escrow Balance</p>
                                      <p className="font-medium font-mono">{formatCurrency(detail.escrow_balance)}</p>
                                    </div>
                                  )}
                                  {detail.past_due_amount > 0 && (
                                    <div>
                                      <p className="text-[10px] uppercase text-red-400 tracking-wider">Past Due</p>
                                      <p className="font-medium font-mono text-red-400">{formatCurrency(detail.past_due_amount)}</p>
                                    </div>
                                  )}
                                  {(detail.ytd_interest_paid != null || detail.ytd_principal_paid != null) && (
                                    <div className="col-span-2">
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Year-to-Date Payments</p>
                                      <p className="font-medium font-mono">
                                        Interest: {formatCurrency(detail.ytd_interest_paid || 0)} | Principal: {formatCurrency(detail.ytd_principal_paid || 0)}
                                      </p>
                                    </div>
                                  )}
                                  <div className="flex gap-3">
                                    {detail.has_pmi && <Badge variant="outline" className="text-[9px]">PMI</Badge>}
                                    {detail.has_prepayment_penalty && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-400">Prepay Penalty</Badge>}
                                    {detail.current_late_fee > 0 && <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-400">Late Fee: {formatCurrency(detail.current_late_fee)}</Badge>}
                                  </div>
                                </>
                              )}
                              {credit.credit_type === "student_loan" && (
                                <>
                                  {detail.loan_name && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Loan Name</p>
                                      <p className="font-medium">{detail.loan_name}</p>
                                    </div>
                                  )}
                                  {detail.loan_status && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Status</p>
                                      <p className="font-medium capitalize">{detail.loan_status}</p>
                                    </div>
                                  )}
                                  {detail.origination_date && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Origination</p>
                                      <p className="font-medium">{detail.origination_date}</p>
                                    </div>
                                  )}
                                  {detail.origination_principal != null && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Original Principal</p>
                                      <p className="font-medium font-mono">{formatCurrency(detail.origination_principal)}</p>
                                    </div>
                                  )}
                                  {detail.expected_payoff_date && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Expected Payoff</p>
                                      <p className="font-medium">{detail.expected_payoff_date}</p>
                                    </div>
                                  )}
                                  {detail.repayment_plan && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Repayment Plan</p>
                                      <p className="font-medium">{detail.repayment_plan}</p>
                                    </div>
                                  )}
                                  {detail.guarantor && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Landmark className="w-3 h-3" />Guarantor</p>
                                      <p className="font-medium">{detail.guarantor}</p>
                                    </div>
                                  )}
                                  {detail.outstanding_interest != null && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Outstanding Interest</p>
                                      <p className="font-medium font-mono">{formatCurrency(detail.outstanding_interest)}</p>
                                    </div>
                                  )}
                                  {detail.next_payment_due_date && (
                                    <div>
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Next Payment Due</p>
                                      <p className="font-medium">{detail.next_payment_due_date}</p>
                                    </div>
                                  )}
                                  {(detail.ytd_interest_paid != null || detail.ytd_principal_paid != null) && (
                                    <div className="col-span-2">
                                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Year-to-Date Payments</p>
                                      <p className="font-medium font-mono">
                                        Interest: {formatCurrency(detail.ytd_interest_paid || 0)} | Principal: {formatCurrency(detail.ytd_principal_paid || 0)}
                                      </p>
                                    </div>
                                  )}
                                  <div className="flex gap-3">
                                    {detail.is_overdue && <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-400">Overdue</Badge>}
                                  </div>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No credits yet</p>
                <Button onClick={() => setIsAddOpen(true)} className="bg-primary text-primary-foreground">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Credit
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] uppercase">{t("credits.editCredit")}</DialogTitle>
              <DialogDescription>Update credit details</DialogDescription>
            </DialogHeader>
            <CreditForm
              formData={formData}
              setFormData={setFormData}
              assets={assets}
              formatCurrency={formatCurrency}
              isEdit
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEdit} className="bg-primary text-primary-foreground">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          onConfirm={handleDelete}
          title="Delete Credit"
          itemName={deleteTarget?.name}
        />
      </div>
    </MainLayout>
  );
};

export default CreditsPage;
